import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { formatCalendarRange, daysBetween, todayLocalDate } from "@/lib/calendarDate";
import {
  LayoutDashboard,
  Ship,
  Compass,
  Users,
  Briefcase,
  Wallet,
  FileText,
  Waves,
  Settings as SettingsIcon,
  Anchor,
  Wifi,
  Sun,
  Cloud,
  Shield,
  ShieldCheck,
  Landmark,
  BookOpen,
  UtensilsCrossed,
  Map as MapIcon,
  Route as RouteIcon,
  ShoppingBag,
  Ticket,
  Camera,
  Newspaper,
  Siren,
} from "lucide-react";
import { useCruise } from "@/context/CruiseContext";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings, formatTemperature } from "@/lib/appSettings";
import { TESTIDS } from "@/lib/testIds";
import { isEditableFocus } from "@/lib/keyboardShortcuts";
import SidebarShipTime from "@/components/layout/SidebarShipTime";
// Brand Package Adoption Phase 1 · canonical brand asset URLs
// (see /app/docs/brand/PHASE-1-ADOPTION-AUDIT.md).
import { BRAND_LOGO, BRAND_META } from "@/lib/BrandAssets";
import { VERSION_META, VERSION_LINE } from "@/lib/version";

// Unified navigation across all lifecycle stages (approved product IA
// baseline · PO 2026-02-20 nav refactor).
//
// Approved primary order:
//   Dashboard → Cruises → Journey → Travelers → Add-Ons →
//   Dining → Excursions → Packing → Finance → Documents →
//   Route Map → Briefing → Settings
//
// The legacy "Cruise Planner" workspace has been retired; its
// remaining responsibilities were reassigned to the Dashboard
// (readiness) and to the dedicated domain workspaces below.
//
// RC7 · 2026-02-11 · The former "Binder" sidebar entry is retired
// as a product concept. It has been repurposed into "Briefing" —
// the personalised daily voyage intelligence surface answering
// "What matters for my cruise right now?". The route target is
// `#briefing` → `<JourneyIntelligence />`. Workspace-owned data
// (bookings, party, documents, flights, hotel, dining, excursions,
// add-ons, packing, finance, route map) is NEVER duplicated here —
// each of those workspaces remains authoritative.
const NAV_ITEMS = [
  // Note (PO 2026-02-11 · Sidebar IA): the former `dashboard` entry
  // was removed from the primary sidebar navigation. Voyage Home is
  // accessed via the Cruise Command brand block at the top of the
  // sidebar (see `handleBrandClick` / `bumpLogoClicks` below). Do
  // NOT reintroduce a `Home` menu item here — the brand block is
  // the canonical affordance.
  { key: "voyages", label: "Cruises", icon: Ship },
  { key: "journey", label: "Journey", icon: Compass },
  { key: "travelers", label: "Travelers", icon: Users },
  // Add-Ons · commercial system of record for onboard purchases.
  // Owns dining packages, beverage packages, internet (VOOM), spa,
  // The Key, photo packages, prepaid gratuities, gifts and future
  // shopping opportunities. Operational workspaces consume from here.
  { key: "add-ons", label: "Add-Ons", icon: ShoppingBag },
  { key: "dining", label: "Dining", icon: UtensilsCrossed },
  { key: "excursions", label: "Excursions", icon: MapIcon },
  // CCD-FIN-016 Phase 1c completion (PO 2026-08-06) · Insurance is the
  // owner of travel-policy records; Finance projects them read-only.
  { key: "insurance", label: "Insurance", icon: ShieldCheck },
  { key: "packing", label: "Packing", icon: Briefcase },
  { key: "finances", label: "Finance", icon: Wallet },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "voyage-map", label: "Route Map", icon: RouteIcon },
  { key: "briefing", label: "Briefing", icon: Newspaper },
  // DDS-001 · Chapter 10 · Emergency is a permanent navigation destination
  // (replaces the floating Safety button). Opens the existing `#emergency`
  // landing via the hash router — no second implementation. Kept directly
  // above Settings; restrained emergency-red accent set by `accent`.
  { key: "emergency", label: "Emergency", icon: Siren, accent: "emergency", ariaLabel: "Emergency assistance" },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  // ── Editorial & Operations (collapsed by default, ~5% of users) ──
  // Curator-facing surfaces. Users who never open these still see the
  // full traveler experience above. Route keys remain unchanged so OCC
  // deep-links, testids, and Priority Now CTAs continue to resolve.
  { key: "ckb-studio", label: "Knowledge Studio", icon: BookOpen, requiresCap: "knowledge:read", group: "editorial" },
  { key: "editorial-studio", label: "Editorial Studio", icon: Camera, requiresCap: "media:manage", group: "editorial" },
  { key: "kqw", label: "Knowledge Quality", icon: LayoutDashboard, requiresCap: "knowledge:read", group: "editorial" },
  { key: "command-centre", label: "Operations Command Centre", icon: LayoutDashboard, requiresCap: "knowledge:read", group: "editorial" },
  // Ops cluster · every domain-specific operations workspace.
  { key: "booking", label: "Booking Ops", icon: Ticket, requiresCap: "knowledge:read", group: "ops" },
  { key: "journey-ops", label: "Journey Ops", icon: Compass, requiresCap: "knowledge:read", group: "ops" },
  { key: "dining-ops", label: "Dining Ops", icon: UtensilsCrossed, requiresCap: "knowledge:read", group: "ops" },
  { key: "entitlements-ops", label: "Entitlements Ops", icon: Shield, requiresCap: "knowledge:read", group: "ops" },
  { key: "governance", label: "Governance", icon: Landmark, requiresCap: "governance:read", group: "editorial" },
  { key: "administration", label: "Administration", icon: Shield, requiresCap: "admin:users:read", group: "editorial" },
  // Legacy `#/knowledge` and `#/knowledge-prototype` deep links are
  // transparently redirected to the canonical Knowledge route in App.js.
];

const STAGES = [
  { key: "planning", label: "Planning" },
  { key: "voyage", label: "My Itinerary" },
  { key: "port-day", label: "Port Day" },
  { key: "memories", label: "Memories" },
];

// Shared row renderer — used for both the editorial and ops clusters.
// Route keys unchanged so testids, OCC deep-links and Priority Now CTAs
// remain stable.
function SidebarNavItem({ item, route, setRoute }) {
  const Icon = item.icon;
  const active = route === item.key || (item.key === "editorial-studio" && route.startsWith("editorial-studio:"));
  return (
    <li>
      <button
        data-testid={TESTIDS.sidebarNavItem(item.key)}
        onClick={() => setRoute(item.key)}
        className={`cc-nav-link w-full text-left ${active ? "is-active" : ""}`}
        style={
          active
            ? {
                background: "#b18a1e",
                color: "#1a1204",
              }
            : {}
        }
      >
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
        <span className="flex-1">{item.label}</span>
      </button>
    </li>
  );
}

export default function Sidebar() {
  const { stage, setStage, route, setRoute, voyageMeta, config, today, firstRun, mobileNavOpen, setMobileNavOpen } = useCruise();
  const { user, hasCapability } = useAuth();
  const { effective: appSettings } = useAppSettings({ enabled: !!user });
  const nav = NAV_ITEMS.filter((n) => !n.requiresCap || hasCapability(n.requiresCap));
  const primaryNav = nav.filter((n) => !n.group);
  const editorialNav = nav.filter((n) => n.group === "editorial");
  const opsNav = nav.filter((n) => n.group === "ops");

  const shipName = voyageMeta?.ship_name || config?.ship_name || "";
  const region = voyageMeta?.region || "";
  // PD-031 · BR-DATE-003 — calendar-date range renders via the shared
  // temporal library. Never construct a JS Date from a calendar value.
  const dateRange = (() => {
    const eA = config?.embark_date ?? config?.embark_at;
    const dA = config?.disembark_date ?? config?.disembark_at;
    if (!eA || !dA) return "";
    return formatCalendarRange(eA, dA);
  })();

  // Today's Brief projections (ADR-007). Undefined until /today resolves —
  // the sidebar renders empty labels rather than misleading placeholders.
  const dayLabel = today?.day?.label || "";
  const wxTempF = today?.environment?.weather?.temp_f ?? today?.weather?.temp_f ?? null;
  const wxCondition = today?.environment?.weather?.condition ?? today?.weather?.condition ?? "";
  const wifiConnected = (today?.environment?.connectivity?.wifi_status || "connected") !== "offline";
  const wxStatus = (() => {
    if (!today) return "";
    const kind = today.day?.kind;
    if (kind === "sea_day") return "At Sea";
    if (kind === "embark") return "Embarking";
    if (kind === "disembark") return "Disembarking";
    const port = today.current_port_call?.port;
    return port ? `In ${port.name}` : "At Sea";
  })();

  // Days until sail — used by the pre-voyage status card in the sidebar.
  // PD-031 · calendar-date arithmetic, no JS Date.
  const daysToEmbark = (() => {
    const composed = today?.planning?.days_to_embark;
    if (composed != null) return composed;
    // Fall back to any source that has the calendar-date embark.
    const embarkDate =
      config?.embark_date ?? config?.embark_at ??
      voyageMeta?.embark_date ?? voyageMeta?.embark_at;
    if (!embarkDate) return null;
    const d = daysBetween(todayLocalDate(), embarkDate);
    return d == null ? null : Math.max(0, d);
  })();

  // Developer Preview Mode — hidden by default. Toggle via keyboard
  // (Shift+Alt+D) OR 5 rapid clicks on the anchor logo. P0 fix Feb 2026:
  // this global keydown listener now bails immediately when the user
  // is typing into an input / textarea / contentEditable so the
  // shortcut cannot swallow a capital "D". Adding Alt to the combo
  // also prevents plain Shift+D (typed capital) from ever firing.
  const [devMode, setDevMode] = useState(() => {
    try {
      return localStorage.getItem("cc-dev-mode") === "1";
    } catch {
      return false;
    }
  });
  const [logoClicks, setLogoClicks] = useState(0);
  // Editorial & Operations cluster is collapsed by default. If the user
  // is currently on any editorial/ops route, auto-open it so the active
  // item is visible.
  const [editorialOpen, setEditorialOpen] = useState(() => {
    const editorialKeys = new Set([
      "ckb-studio", "editorial-studio", "kqw", "command-centre", "governance", "administration",
      "booking", "journey-ops", "dining-ops", "entitlements-ops",
    ]);
    return editorialKeys.has(route) || route.startsWith("editorial-studio:");
  });
  useEffect(() => {
    const editorialKeys = new Set([
      "ckb-studio", "editorial-studio", "kqw", "command-centre", "governance", "administration",
      "booking", "journey-ops", "dining-ops", "entitlements-ops",
    ]);
    if (editorialKeys.has(route) || route.startsWith("editorial-studio:")) setEditorialOpen(true);
  }, [route]);

  useEffect(() => {
    const onKey = (e) => {
      if (isEditableFocus(e)) return; // never intercept while typing
      // Require Shift + Alt to avoid colliding with plain Shift+D as
      // typed capital letters. Alt raises the bar to a deliberate
      // developer keystroke.
      if (e.shiftKey && e.altKey && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        setDevMode((v) => {
          const next = !v;
          try {
            localStorage.setItem("cc-dev-mode", next ? "1" : "0");
          } catch (_e) { /* ignore */ }
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const bumpLogoClicks = () => {
    setLogoClicks((c) => {
      const nc = c + 1;
      if (nc >= 5) {
        setDevMode((v) => {
          const next = !v;
          try {
            localStorage.setItem("cc-dev-mode", next ? "1" : "0");
          } catch (_e) { /* ignore */ }
          return next;
        });
        return 0;
      }
      return nc;
    });
    setTimeout(() => setLogoClicks(0), 2000);
  };

  // PO 2026-02-11 · Sidebar IA — the Cruise Command brand block is the
  // primary affordance for returning to Voyage Home. Clicking or
  // keyboard-activating the block routes to the existing `dashboard`
  // route (which renders Voyage Home). The 5-click dev-mode gesture
  // continues to work — dev users just navigate to Voyage Home each
  // click, which is a no-op after the first (setRoute is idempotent
  // for the same key), and the counter still triggers after five.
  const handleBrandActivate = () => {
    bumpLogoClicks();
    setRoute("dashboard");
    // Close the mobile drawer if it is open — the brand block is the
    // primary "back to voyage home" affordance and travellers expect
    // it to also dismiss the overlay.
    if (mobileNavOpen) setMobileNavOpen(false);
  };
  const handleBrandKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleBrandActivate();
    }
  };

  return (
    <>
      {/* RC2 · MXR-001 · Mobile backdrop — dismisses the drawer on tap. */}
      {mobileNavOpen && (
        <div
          data-testid="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm transition-opacity"
          aria-hidden
        />
      )}
      <aside
        data-testid={TESTIDS.sidebar}
        className={
          "fixed inset-y-0 left-0 z-40 w-64 flex flex-col text-white overflow-hidden " +
          "transition-transform duration-300 ease-out " +
          "md:translate-x-0 " +
          (mobileNavOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full")
        }
        style={{
          background: "var(--cc-sidebar-bg)",
          // Almost-invisible wave texture — luxury-yacht feeling.
          // Two staggered radial gradients simulate light on lacquer,
          // one very-soft horizontal wave gives the surface direction.
          backgroundImage:
            "radial-gradient(1200px 400px at -10% -10%, rgba(255,255,255,0.045), transparent 60%)," +
            "radial-gradient(900px 320px at 110% 20%, rgba(181,136,0,0.055), transparent 55%)," +
            "repeating-linear-gradient(115deg, rgba(255,255,255,0.014) 0 1px, transparent 1px 22px)," +
            "var(--cc-sidebar-bg)",
        }}
      >
      {/* Logo · official brand mark (SVG, reverse variant designed for
          dark backgrounds — white wordmark with the WARM GOLD accent
          preserved). Sourced from BRAND_LOGO.reverse which maps to
          public/brand/logo/logo-dark.svg (naming convention: "-dark"
          = for use ON dark backgrounds). Do NOT swap to BRAND_LOGO.white
          — that is a pure monochrome knockout and drops the gold
          accent required by BR-BRAND-001.

          Sizing: ~60px height (roughly +50% over the initial 40px) with
          generous vertical padding to elevate the sidebar from admin-
          panel to premium-product register. Left-aligned with nav content.

          PO 2026-08-07 · Sidebar Branding Layout — tightened the gap
          between the logo image and the Voyage Intelligence™ subtitle
          so the subtitle reads as a product subtitle rather than a
          floating element. Vertical space reclaimed goes to the nav. */}
      <div
        data-testid={TESTIDS.sidebarLogo}
        role="button"
        tabIndex={0}
        aria-label="Return to Voyage Home"
        title="Voyage Home"
        className="px-6 pt-5 pb-3 select-none cursor-pointer rounded-md outline-none transition-colors hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-[#d4af37]/60 focus-visible:ring-offset-0"
        onClick={handleBrandActivate}
        onKeyDown={handleBrandKeyDown}
      >
        {/* Brand lockup — the SVG logo carries the full "CRUISE COMMAND"
            wordmark, so we do NOT repeat it below as text. Only the
            supporting tagline and a subtle version stamp remain.
            Reference: Linear / Notion / Stripe Dashboard / Raycast. */}
        <img loading="lazy" decoding="async"
          src={BRAND_LOGO.reverse}
          alt={BRAND_META.productName}
          className="h-[110px] w-auto max-w-full select-none block mb-1.5"
          draggable={false}
        />
        <div
          data-testid="sidebar-brand-name"
          className="text-[11px] tracking-[0.14em] text-white/55 leading-none"
        >
          Voyage Intelligence<span className="text-white/35 ml-0.5">™</span>
        </div>
        <div
          data-testid="sidebar-version-badge"
          className="mt-1.5 text-[9px] tracking-[0.20em] uppercase font-normal text-white/25 leading-none"
          title={`${VERSION_META.productName} ${VERSION_META.version} · ${VERSION_META.status}`}
        >
          <span data-testid="sidebar-version-chip">Version {VERSION_META.version} {VERSION_META.build}</span>
        </div>
      </div>

      {/* Breathing room · minimal gap so the logo block reads as a
          distinct section without stealing space from the navigation.
          Reduced from h-2.5 → h-1 (PO 2026-08-07). */}
      <div className="h-1" aria-hidden="true" />

      {/* Nav */}
      <nav className="px-3 pb-2 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {primaryNav.map((item) => {
            const Icon = item.icon;
            const active = route === item.key;
            const isEmergency = item.accent === "emergency";
            return (
              <li key={item.key}>
                <button
                  data-testid={TESTIDS.sidebarNavItem(item.key)}
                  onClick={() => setRoute(item.key)}
                  title={item.ariaLabel || undefined}
                  aria-label={item.ariaLabel || undefined}
                  className={`cc-nav-link w-full text-left ${isEmergency ? "cc-nav-link--emergency" : ""} ${active ? "is-active" : ""}`}
                  style={
                    active && !isEmergency
                      ? {
                          background: "#1e6de5",
                          color: "#fff",
                        }
                      : {}
                  }
                >
                  <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#1e6de5] text-white text-[10px] font-bold">
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {(editorialNav.length > 0 || opsNav.length > 0) && (
          <>
            <button
              type="button"
              className="mt-5 mb-2 px-3 flex items-center gap-2 w-full text-left group"
              onClick={() => setEditorialOpen((v) => !v)}
              data-testid="sidebar-editorial-toggle"
              aria-expanded={editorialOpen}
            >
              <div className="cc-overline text-[#D4AF37]/70 text-[9px] group-hover:text-[#D4AF37]">
                Editorial &amp; Operations {editorialOpen ? "▾" : "▸"}
              </div>
              <div className="flex-1 h-px bg-white/10" />
            </button>
            {editorialOpen && (
              <>
                {editorialNav.length > 0 && (
                  <ul className="space-y-1">
                    {editorialNav.map((item) => (
                      <SidebarNavItem key={item.key} item={item} route={route} setRoute={setRoute} />
                    ))}
                  </ul>
                )}
                {opsNav.length > 0 && (
                  <>
                    <div className="mt-3 mb-1 px-3 flex items-center gap-2">
                      <div className="cc-overline text-white/40 text-[9px]">Ops</div>
                      <div className="flex-1 h-px bg-white/5" />
                    </div>
                    <ul className="space-y-1">
                      {opsNav.map((item) => (
                        <SidebarNavItem key={item.key} item={item} route={route} setRoute={setRoute} />
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* My Profile — sits after nav in planning */}
      </nav>

      {/* Stage-specific bottom card */}
      <div className="px-4 pb-4 space-y-3">
        {/* Iter 63A · Sidebar Account Card removed — user identity now
            lives exclusively in the top-right header (single source of
            truth). The sidebar flows directly from navigation to the
            Voyage Status card. */}

        {/* Voyage Status card — contextual by lifecycle stage.
            Hidden on the Dashboard (which already shows the same info
            in its hero) so it never duplicates the current workspace.
            Also hidden entirely in first-run so we never fabricate
            countdown/dates from placeholder voyage config (PD-022).

            PO 2026-08-07 · Sidebar Refinement — compacted from a
            feature-panel register to a persistent contextual reminder.
            Height reduced ~30% via tighter padding, smaller count
            display, and removal of the redundant Region row (region
            already surfaces on the Dashboard hero and Route Map).
            Typography hierarchy preserved; nav retains primacy. */}
        {!firstRun && stage === "planning" && route !== "dashboard" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5" data-testid="sidebar-voyage-card-planning">
            <div className="cc-overline text-[#D4AF37] mb-1">Pre-Voyage</div>
            {daysToEmbark != null ? (
              <div className="flex items-baseline gap-1.5">
                <span className="font-display font-black text-[22px] leading-none text-white tabular-nums">
                  {daysToEmbark}
                </span>
                <span className="text-[10.5px] font-semibold text-white/70 uppercase tracking-wider">
                  day{daysToEmbark === 1 ? "" : "s"} to sail
                </span>
              </div>
            ) : (
              <div className="text-[12.5px] font-semibold text-white/85">Voyage details loading…</div>
            )}
            {shipName && (
              <div className="text-[11.5px] text-white/85 mt-1.5 truncate">{shipName}</div>
            )}
            {dateRange && (
              <div className="text-[10.5px] text-white/55 mt-0.5">{dateRange}</div>
            )}
          </div>
        )}

        {!firstRun && (stage === "voyage" || stage === "port-day") && (
          <>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="cc-overline text-white/50 mb-2">Current Voyage</div>
              <div className="text-[14px] font-semibold leading-snug">
                {region}
              </div>
              <div className="text-[11px] text-white/70 mt-1">{shipName}</div>
              <div className="text-[11px] text-white/50 mt-1">{dateRange}</div>
              <div className="text-[12px] mt-2 font-semibold text-[#5aa8ff]">
                {dayLabel}
              </div>
            </div>
            <SidebarShipTime today={today} />
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3">
              <Wifi className={`w-5 h-5 ${wifiConnected ? "text-[#22C55E]" : "text-white/50"}`} />
              <div className="leading-tight">
                <div className="text-[12px] font-semibold">{today?.environment?.connectivity?.label || "Ship Wi-Fi"}</div>
                <div className="text-[10px] text-white/55">{wifiConnected ? "Connected" : "Offline"}</div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center">
                <Sun className="w-4 h-4 text-[#F59E0B]" />
                <Cloud className="w-3 h-3 text-white/70 -ml-1.5 mt-2" />
              </div>
              <div className="leading-tight">
                <div className="text-[13px] font-semibold tabular-nums">
                  {wxTempF != null ? formatTemperature(wxTempF, appSettings) : "—"}
                </div>
                <div className="text-[10px] text-white/55">{wxCondition}</div>
                <div className="text-[10px] text-white/40">{wxStatus}</div>
              </div>
            </div>
          </>
        )}

        {!firstRun && stage === "memories" && route !== "dashboard" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="cc-overline text-white/50 mb-2">Voyage Complete</div>
            <div className="text-[14px] font-semibold leading-snug">
              {region}
            </div>
            <div className="text-[11px] text-white/70 mt-1">{shipName}</div>
            <div className="text-[11px] text-white/50 mt-1">{dateRange}</div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#22C55E]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" /> Completed
            </div>
          </div>
        )}

        {/* Developer Preview Mode — hidden by default; press ⇧+D to toggle */}
        {devMode && (
        <div
          data-testid={TESTIDS.demoSwitcher}
          className="rounded-2xl border border-white/10 bg-black/25 p-1.5"
        >
          <div className="cc-overline text-white/40 text-center text-[9px] mb-1.5">
            {VERSION_LINE}
          </div>
          <div className="relative grid grid-cols-4 gap-1">
            {STAGES.map((s) => {
              const active = stage === s.key;
              const testId =
                s.key === "planning"
                  ? TESTIDS.demoSwitcherPlanning
                  : s.key === "voyage"
                    ? TESTIDS.demoSwitcherVoyage
                    : s.key === "port-day"
                      ? TESTIDS.demoSwitcherPortDay
                      : TESTIDS.demoSwitcherMemories;
              return (
                <button
                  key={s.key}
                  data-testid={testId}
                  onClick={() => setStage(s.key)}
                  className="relative text-[10px] font-semibold py-1.5 rounded-lg text-white/70 hover:text-white transition-colors"
                >
                  {active && (
                    <motion.span
                      layoutId="cc-stage-pill"
                      className="absolute inset-0 rounded-lg"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(212,175,55,0.95), rgba(212,175,55,0.55))",
                        boxShadow: "0 4px 12px rgba(212,175,55,0.35)",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 30,
                      }}
                    />
                  )}
                  <span className={`relative ${active ? "text-[#1a1204]" : ""}`}>
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Sidebar version footer removed 2026-08-07 (PO Sidebar
            Refinement) — the RC version chip beneath the logo is the
            single source of truth. GA plan: the logo-adjacent version
            chip itself moves to Settings → About once RC builds
            complete. Do not reintroduce the footer version here. */}
      </div>
    </aside>
    </>
  );
}
